import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Badge,
  ProgressBar
} from 'react-bootstrap';
import axios from 'axios';
import { useDrums } from '../../features/drums/hooks/useDrums.js';
import {
  getDrumCapacityMeters,
  getCableOuterDiameter,
  computeCableWeightKg
} from '../../helpers/drumCapacity.js';
import { buildDeliverySummary } from '../../helpers/deliverySummary.js';
import { API } from '../../config/api';
import { formatProductLabel } from '../../helpers/productLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { drumLengthStandardUrl } from '../../helpers/drumLengthLink';

const drumMaterials = [
  { value: 'wood', label: 'Wood' },
  { value: 'metal', label: 'Metal' },
  { value: 'plastic', label: 'Plastic' }
];
const drumDiameters = [600, 800, 1000, 1250, 1400, 1600, 1800, 2000];

const toNum = v => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isFinite(n) ? n : '';
};

const safeArr = v => (Array.isArray(v) ? v : []);

const sumPlanMeters = plan => {
  const drums = safeArr(plan?.drums);
  const coils = safeArr(plan?.coils);

  const drumsSum = drums.reduce((acc, d) => {
    const qty = Number(d.quantity) || 0;
    const len = Number(d.lengthPerDrum) || 0;
    return acc + qty * len;
  }, 0);

  const coilsSum = coils.reduce((acc, c) => {
    const qty = Number(c.quantity) || 0;
    const len = Number(c.lengthPerCoil) || 0;
    return acc + qty * len;
  }, 0);

  return drumsSum + coilsSum;
};

// shared builder — reads items[] (DRUM + COIL) so tamburi aren't dropped
const buildPlanSummary = buildDeliverySummary;

const fmt = n => {
  const x = Number(n) || 0;
  return x.toLocaleString();
};

const DeliveryPlanModal = ({
  show,
  onHide,
  product,
  onSave // (updatedDeliveryPlan) => void
}) => {
  const [localPlan, setLocalPlan] = useState(
    () => product?.deliveryPlan || { drums: [], coils: [], palletPlan: [] }
  );
  const [localErr, setLocalErr] = useState('');

  React.useEffect(() => {
    setLocalPlan(product?.deliveryPlan || { drums: [], coils: [], palletPlan: [] });
    setLocalErr('');
  }, [product, show]);

  // ── Standard drums (from DB) + this product's standard (for cable Ø + weight) ──
  const { data: drumsData } = useDrums();
  const drumLibrary = useMemo(() => drumsData?.data || [], [drumsData]);

  // fetch the full standard (phases → finished cable outer Ø + material weights)
  const [standard, setStandard] = useState(null);
  useEffect(() => {
    if (!show || !product?.cableName) {
      setStandard(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API.BACKEND}/api/v1/standard/find-many`,
          [
            {
              name: product.cableName,
              numberOfConductors: product.numberOfConductors,
              section: product.section,
              color: product.color,
              hasMixedConductorSections: !!product.hasMixedConductorSections,
              greenYellowSection: product.hasMixedConductorSections
                ? product.greenYellowSection
                : null
            }
          ]
        );
        const std = (res?.data?.data || [])[0] || null;
        if (!cancelled) setStandard(std);
      } catch {
        if (!cancelled) setStandard(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, product]);

  const cableOuterDiameter = useMemo(
    () => getCableOuterDiameter(standard?.phases),
    [standard]
  );

  // standard drum material options + per-material diameters (from the library)
  const drumMaterialOptions = useMemo(() => {
    const set = new Set(
      (drumLibrary || [])
        .map(d => String(d.drumType || '').toLowerCase())
        .filter(Boolean)
    );
    return Array.from(set);
  }, [drumLibrary]);

  const diametersForMaterial = material => {
    const mat = String(material || '').toLowerCase();
    return Array.from(
      new Set(
        (drumLibrary || [])
          .filter(d => String(d.drumType || '').toLowerCase() === mat)
          .map(d => Number(d.drumDiameter))
          .filter(Boolean)
      )
    ).sort((a, b) => a - b);
  };

  const capLabel = s => s.charAt(0).toUpperCase() + s.slice(1);

  // max meters of the finished cable that fit on the chosen standard drum
  const drumCapacityFor = d =>
    getDrumCapacityMeters({
      drumMaterial: d.drumMaterial,
      drumDiameter: d.drumDiameter,
      cableDiameter: cableOuterDiameter,
      drumLibrary
    });

  const totalPlanned = useMemo(() => sumPlanMeters(localPlan), [localPlan]);
  const target = Number(product?.totalOrderedLength) || 0;
  const diff = target - totalPlanned;

  const isValidTotal = target > 0 && totalPlanned === target;

  const summaryPreview = useMemo(
    () => buildPlanSummary(localPlan),
    [localPlan]
  );

  // progress UI
  const progressPct = useMemo(() => {
    if (!target) return 0;
    const pct = (totalPlanned / target) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [totalPlanned, target]);

  const progressVariant = useMemo(() => {
    if (!target) return 'secondary';
    if (isValidTotal) return 'success';
    if (totalPlanned === 0) return 'secondary';
    if (totalPlanned < target) return 'warning';
    return 'danger'; // over
  }, [target, isValidTotal, totalPlanned]);

  const diffBadge = useMemo(() => {
    if (!target) return { text: 'No target', bg: 'secondary' };
    if (diff === 0) return { text: 'Perfect match', bg: 'success' };
    if (diff > 0) return { text: `Missing +${fmt(diff)}m`, bg: 'warning' };
    return { text: `Over ${fmt(Math.abs(diff))}m`, bg: 'danger' };
  }, [diff, target]);

  // ---------------- DRUMS ----------------
  const addDrumRow = () => {
    setLocalPlan(prev => ({
      ...prev,
      drums: [
        ...safeArr(prev.drums),
        { drumMaterial: '', drumDiameter: '', lengthPerDrum: '', quantity: '' }
      ]
    }));
  };

  const updateDrum = (idx, key, value) => {
    setLocalPlan(prev => ({
      ...prev,
      drums: safeArr(prev.drums).map((d, i) =>
        i === idx
          ? {
              ...d,
              [key]:
                key === 'quantity' || key === 'lengthPerDrum' || key === 'drumDiameter'
                  ? toNum(value)
                  : value
            }
          : d
      )
    }));
  };

  const removeDrumRow = idx => {
    setLocalPlan(prev => ({
      ...prev,
      drums: safeArr(prev.drums).filter((_, i) => i !== idx)
    }));
  };

  // ---------------- COILS ----------------
  const addCoilRow = () => {
    setLocalPlan(prev => ({
      ...prev,
      coils: [...safeArr(prev.coils), { lengthPerCoil: '', quantity: '' }]
    }));
  };

  const updateCoil = (idx, key, value) => {
    setLocalPlan(prev => ({
      ...prev,
      coils: safeArr(prev.coils).map((c, i) =>
        i === idx
          ? {
              ...c,
              [key]:
                key === 'quantity' || key === 'lengthPerCoil'
                  ? toNum(value)
                  : value
            }
          : c
      )
    }));
  };

  const removeCoilRow = idx => {
    setLocalPlan(prev => ({
      ...prev,
      coils: safeArr(prev.coils).filter((_, i) => i !== idx)
    }));
  };

  // ---- PALLET PLAN (optional, for coils) ----
  const hasCoils = safeArr(localPlan.coils).some(c => Number(c.quantity) > 0 && Number(c.lengthPerCoil) > 0);

  const addPalletRow = () => {
    setLocalPlan(prev => {
      // Born complete (like the Split action) so a manually-added row is a valid,
      // preserved pallet config out of the box — coil length + remaining unassigned
      // coils on one pallet — instead of an empty row that gets dropped on save.
      const coils = safeArr(prev.coils).filter(
        c => Number(c.quantity) > 0 && Number(c.lengthPerCoil) > 0
      );
      const byLen = {};
      coils.forEach(c => {
        const l = Number(c.lengthPerCoil);
        byLen[l] = (byLen[l] || 0) + Number(c.quantity);
      });
      const domLen =
        Number(Object.entries(byLen).sort((a, b) => b[1] - a[1])[0]?.[0]) || '';
      const totalC = coils.reduce((s, c) => s + Number(c.quantity), 0);
      const assigned = safeArr(prev.palletPlan).reduce(
        (s, p) => s + (Number(p.coilsPerPallet) || 0) * (Number(p.palletCount) || 0),
        0
      );
      const remaining = Math.max(0, totalC - assigned);
      return {
        ...prev,
        palletPlan: [
          ...safeArr(prev.palletPlan),
          {
            coilLength: domLen,
            coilsPerPallet: remaining > 0 ? remaining : '',
            palletCount: remaining > 0 ? 1 : '',
            notes: ''
          }
        ]
      };
    });
  };

  const updatePallet = (idx, key, value) => {
    setLocalPlan(prev => ({
      ...prev,
      palletPlan: safeArr(prev.palletPlan).map((p, i) =>
        i === idx ? { ...p, [key]: key === 'notes' ? value : toNum(value) } : p
      )
    }));
  };

  const removePalletRow = idx => {
    setLocalPlan(prev => ({
      ...prev,
      palletPlan: safeArr(prev.palletPlan).filter((_, i) => i !== idx)
    }));
  };

  // Quick "split the coil delivery into N equal pallets" (user thinks in
  // pallets, e.g. "2 pallets of 6000 m"). Uses the dominant coil length when
  // coils vary; the planner can still tweak rows or add more for mixed lengths.
  const [splitN, setSplitN] = useState(2);
  const splitIntoPallets = () => {
    const n = Math.max(1, Math.floor(Number(splitN) || 1));
    const coils = safeArr(localPlan.coils).filter(
      c => Number(c.quantity) > 0 && Number(c.lengthPerCoil) > 0
    );
    if (!coils.length) return;
    const total = coils.reduce((s, c) => s + Number(c.quantity), 0);
    const byLen = {};
    coils.forEach(c => {
      const l = Number(c.lengthPerCoil);
      byLen[l] = (byLen[l] || 0) + Number(c.quantity);
    });
    const domLen =
      Number(Object.entries(byLen).sort((a, b) => b[1] - a[1])[0]?.[0]) || 0;
    const coilsPerPallet = Math.ceil(total / n);
    setLocalPlan(prev => ({
      ...prev,
      palletPlan: [
        { coilsPerPallet, coilLength: domLen, palletCount: n, notes: '' }
      ]
    }));
  };

  // Auto-suggest: compute total coils from coils section
  const totalCoils = useMemo(() =>
    safeArr(localPlan.coils).reduce((s, c) => s + (Number(c.quantity) || 0), 0)
  , [localPlan.coils]);

  const totalPalletCoils = useMemo(() =>
    safeArr(localPlan.palletPlan).reduce((s, p) => s + (Number(p.coilsPerPallet) || 0) * (Number(p.palletCount) || 0), 0)
  , [localPlan.palletPlan]);

  const handleSave = () => {
    setLocalErr('');

    if (!target || target <= 0) {
      setLocalErr('Total Ordered Length is missing on product.');
      return;
    }

    const badDrum = safeArr(localPlan.drums).some(d => {
      const qty = Number(d.quantity);
      const len = Number(d.lengthPerDrum);
      const hasSomething = d.drumMaterial || qty || len;
      return hasSomething && (qty <= 0 || len <= 0);
    });

    const badCoil = safeArr(localPlan.coils).some(c => {
      const qty = Number(c.quantity);
      const len = Number(c.lengthPerCoil);
      return (qty && len <= 0) || (len && qty <= 0) || qty < 0 || len < 0;
    });

    if (badDrum) {
      setLocalErr(
        'Check Drum rows: DrumType + Quantity + LengthPerDrum must be all set and > 0.'
      );
      return;
    }
    if (badCoil) {
      setLocalErr(
        'Check Coil rows: Quantity and LengthPerCoil must be both set and > 0.'
      );
      return;
    }

    if (!isValidTotal) {
      setLocalErr(
        `Total mismatch. Planned=${totalPlanned}m, Required=${target}m (diff ${diff > 0 ? '+' : ''}${diff}m).`
      );
      return;
    }

    onSave({
      drums: safeArr(localPlan.drums).map(d => ({
        drumMaterial: String(d.drumMaterial || ''),
        drumDiameter: Number(d.drumDiameter) || null,
        quantity: Number(d.quantity) || 0,
        lengthPerDrum: Number(d.lengthPerDrum) || 0
      })),
      coils: safeArr(localPlan.coils).map(c => ({
        quantity: Number(c.quantity) || 0,
        lengthPerCoil: Number(c.lengthPerCoil) || 0
      })),
      // Keep any row the planner started (coilsPerPallet set); default an omitted
      // pallet count to 1 instead of silently dropping the whole custom config.
      palletPlan: safeArr(localPlan.palletPlan).filter(p =>
        Number(p.coilsPerPallet) > 0
      ).map(p => ({
        coilsPerPallet: Number(p.coilsPerPallet) || 0,
        coilLength: Number(p.coilLength) || 0,
        palletCount: Number(p.palletCount) > 0 ? Number(p.palletCount) : 1,
        notes: p.notes || ''
      })),
      summary: buildPlanSummary(localPlan),
      plannedTotal: totalPlanned
    });

    onHide();
  };

  const productLabel = useMemo(() => {
    if (!product) return '';
    return formatProductLabel(product);
  }, [product]);

  // row status helpers
  const drumRowStatus = d => {
    const hasAny = d?.drumMaterial || d?.drumDiameter || d?.quantity || d?.lengthPerDrum;
    const ok =
      Number(d.quantity) > 0 && Number(d.lengthPerDrum) > 0;

    if (!hasAny) return { text: 'Empty', bg: 'secondary' };
    if (ok) return { text: 'OK', bg: 'success' };
    return { text: 'Incomplete', bg: 'warning' };
  };

  const coilRowStatus = c => {
    const hasAny = c?.quantity || c?.lengthPerCoil;
    const ok = Number(c.quantity) > 0 && Number(c.lengthPerCoil) > 0;

    if (!hasAny) return { text: 'Empty', bg: 'secondary' };
    if (ok) return { text: 'OK', bg: 'success' };
    return { text: 'Incomplete', bg: 'warning' };
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-body-tertiary">
        <Modal.Title className="d-flex align-items-center gap-2">
          Delivery Plan
          <Badge bg="primary" className="ms-1">
            {product?.cableName || 'Product'}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* TOP CARD */}
        <div className="p-3 rounded border bg-light">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <div className="fw-bold fs-5">{productLabel}</div>
              <div className="text-muted">
                {product?.color ? (
                  <Badge bg="dark" className="me-2">
                    {String(product.color).toUpperCase()}
                  </Badge>
                ) : (
                  <Badge bg="secondary" className="me-2">
                    NO COLOR
                  </Badge>
                )}

                <Badge bg={diffBadge.bg}>{diffBadge.text}</Badge>
              </div>
            </div>

            <div className="text-end">
              <div className="small text-muted">Required</div>
              <div className="fw-bold">{fmt(target)} m</div>
              <div className="small text-muted mt-1">Planned</div>
              <div className="fw-bold">{fmt(totalPlanned)} m</div>
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>Progress</span>
              <span>
                {fmt(totalPlanned)} / {fmt(target)} m
              </span>
            </div>
            <ProgressBar
              now={progressPct}
              variant={progressVariant}
              style={{ height: 10, borderRadius: 999 }}
            />
            {!isValidTotal && target > 0 && (
              <div className="small text-muted mt-2">
                Tip: completează Drums/Coils până ajungi exact la totalul cerut.
              </div>
            )}
          </div>

          {summaryPreview && (
            <div className="mt-3 p-2 rounded border bg-white">
              <div className="small text-muted">Summary preview</div>
              <div className="fw-semibold">{summaryPreview}</div>
            </div>
          )}

          {isValidTotal && (
            <div className="mt-3 p-2 rounded border border-success bg-success bg-opacity-10">
              <span className="fw-semibold text-success">
                ✅ Plan OK — totalul planificat este exact.
              </span>
            </div>
          )}
        </div>

        {localErr && (
          <Alert className="mt-3" variant="danger">
            {localErr}
          </Alert>
        )}

        {/* DRUMS */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <h5 className="mb-0">Drums</h5>
            <div className="text-muted small">
              Definește tamburi (tip, lungime/tambur, cantitate)
              {cableOuterDiameter > 0 && (
                <> · finished cable Ø ≈ {cableOuterDiameter} mm</>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline-primary" onClick={addDrumRow}>
            + Add Drum row
          </Button>
        </div>

        <Table bordered hover responsive striped className="mt-2 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: '18%' }}>Material</th>
              <th style={{ width: '18%' }}>Diameter</th>
              <th style={{ width: '18%' }}>Length / Drum (m)</th>
              <th style={{ width: '14%' }}>Quantity</th>
              <th style={{ width: '14%' }}>Total (m)</th>
              <th style={{ width: '12%' }}>Status</th>
              <th style={{ width: '1%' }} />
            </tr>
          </thead>
          <tbody>
            {safeArr(localPlan.drums).length === 0 ? (
              <tr>
                <td colSpan={7} className="text-muted">
                  No drum rows.
                </td>
              </tr>
            ) : (
              safeArr(localPlan.drums).map((d, idx) => {
                const rowTotal =
                  (Number(d.quantity) || 0) * (Number(d.lengthPerDrum) || 0);
                const st = drumRowStatus(d);

                return (
                  <tr key={idx}>
                    <td>
                      <Form.Select
                        value={d.drumMaterial || ''}
                        onChange={e =>
                          updateDrum(idx, 'drumMaterial', e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {(drumMaterialOptions.length
                          ? drumMaterialOptions.map(m => ({
                              value: m,
                              label: capLabel(m)
                            }))
                          : drumMaterials
                        ).map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>

                    <td>
                      <Form.Select
                        value={d.drumDiameter || ''}
                        onChange={e =>
                          updateDrum(idx, 'drumDiameter', e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {(() => {
                          const opts = diametersForMaterial(d.drumMaterial);
                          return (opts.length ? opts : drumDiameters).map(dia => (
                            <option key={dia} value={dia}>
                              {dia} mm
                            </option>
                          ));
                        })()}
                      </Form.Select>
                    </td>

                    <td>
                      <Form.Control
                        type="number"
                        value={d.lengthPerDrum}
                        onChange={e =>
                          updateDrum(idx, 'lengthPerDrum', e.target.value)
                        }
                        min={0}
                      />
                      {(() => {
                        const cap = drumCapacityFor(d);
                        const w = computeCableWeightKg(
                          standard?.phases,
                          d.lengthPerDrum
                        );
                        if (cap == null && w == null) return null;
                        return (
                          <div
                            className="small text-muted mt-1"
                            style={{ fontSize: 11 }}
                          >
                            {cap != null && (
                              <>
                                max ≈ {Math.floor(cap).toLocaleString()} m
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 ms-1 align-baseline"
                                  style={{ fontSize: 11 }}
                                  onClick={() =>
                                    updateDrum(
                                      idx,
                                      'lengthPerDrum',
                                      Math.floor(cap)
                                    )
                                  }
                                >
                                  use
                                </Button>
                              </>
                            )}
                            {w != null && (
                              <span className="ms-1">· ≈ {fmt(w)} kg/drum</span>
                            )}
                            {/* The capacity above is one number. This opens the
                                page that shows the winding itself — layers,
                                turns and the drum in 3D — with the cable, the
                                finished-cable layer, this drum and this length
                                already filled in.

                                A new tab on purpose: the plan stays open beside
                                it, which is why the state travels in the URL
                                (router state does not survive target=_blank).
                                See helpers/drumLengthLink.js. */}
                            {(d.drumMaterial || d.drumDiameter) && (
                              <a
                                className="ms-2"
                                style={{ fontSize: 11 }}
                                href={drumLengthStandardUrl({
                                  product,
                                  drumMaterial: d.drumMaterial,
                                  drumDiameter: d.drumDiameter,
                                  length: d.lengthPerDrum
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open the drum-length page for this cable and drum, in 3D"
                              >
                                <FontAwesomeIcon
                                  icon={faUpRightFromSquare}
                                  className="me-1"
                                />
                                see it on the drum
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    <td>
                      <Form.Control
                        type="number"
                        value={d.quantity}
                        onChange={e =>
                          updateDrum(idx, 'quantity', e.target.value)
                        }
                        min={0}
                      />
                    </td>

                    <td className="text-end fw-semibold">
                      {rowTotal ? rowTotal.toLocaleString() : '0'}
                      {(() => {
                        const wTot = computeCableWeightKg(
                          standard?.phases,
                          rowTotal
                        );
                        return wTot != null ? (
                          <div
                            className="small text-muted fw-normal"
                            style={{ fontSize: 11 }}
                          >
                            ≈ {fmt(wTot)} kg
                          </div>
                        ) : null;
                      })()}
                    </td>

                    <td>
                      <Badge bg={st.bg}>{st.text}</Badge>
                    </td>

                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeDrumRow(idx)}
                      >
                        ✖
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        {/* COILS */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <h5 className="mb-0">Coils</h5>
            <div className="text-muted small">
              Definește colaci (lungime/colac, cantitate)
            </div>
          </div>
          <Button size="sm" variant="outline-primary" onClick={addCoilRow}>
            + Add Coil row
          </Button>
        </div>

        <Table bordered hover responsive striped className="mt-2 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: '30%' }}>Length / Coil (m)</th>
              <th style={{ width: '25%' }}>Quantity</th>
              <th style={{ width: '25%' }}>Total (m)</th>
              <th style={{ width: '15%' }}>Status</th>
              <th style={{ width: '1%' }} />
            </tr>
          </thead>
          <tbody>
            {safeArr(localPlan.coils).length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted">
                  No coil rows.
                </td>
              </tr>
            ) : (
              safeArr(localPlan.coils).map((c, idx) => {
                const rowTotal =
                  (Number(c.quantity) || 0) * (Number(c.lengthPerCoil) || 0);
                const st = coilRowStatus(c);

                return (
                  <tr key={idx}>
                    <td>
                      <Form.Control
                        type="number"
                        value={c.lengthPerCoil}
                        onChange={e =>
                          updateCoil(idx, 'lengthPerCoil', e.target.value)
                        }
                        min={0}
                      />
                    </td>

                    <td>
                      <Form.Control
                        type="number"
                        value={c.quantity}
                        onChange={e =>
                          updateCoil(idx, 'quantity', e.target.value)
                        }
                        min={0}
                      />
                    </td>

                    <td className="text-end fw-semibold">
                      {rowTotal ? rowTotal.toLocaleString() : '0'}
                      {(() => {
                        const wTot = computeCableWeightKg(
                          standard?.phases,
                          rowTotal
                        );
                        return wTot != null ? (
                          <div
                            className="small text-muted fw-normal"
                            style={{ fontSize: 11 }}
                          >
                            ≈ {fmt(wTot)} kg
                          </div>
                        ) : null;
                      })()}
                    </td>

                    <td>
                      <Badge bg={st.bg}>{st.text}</Badge>
                    </td>

                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeCoilRow(idx)}
                      >
                        ✖
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
        {/* PALLET PLAN (only when coils are defined) */}
        {hasCoils && (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div>
                <h5 className="mb-0">Pallet Configuration</h5>
                <div className="text-muted small">
                  Optional — define how coils should be packed on europallets
                </div>
              </div>
              <div className="d-flex align-items-end gap-2">
                <div>
                  <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>
                    Split into N pallets
                  </Form.Label>
                  <div className="d-flex gap-1">
                    <Form.Control
                      type="number"
                      min={1}
                      size="sm"
                      style={{ width: 70 }}
                      value={splitN}
                      onChange={e => setSplitN(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={splitIntoPallets}
                      title="Split the coil delivery into N equal pallets"
                    >
                      Split
                    </Button>
                  </div>
                </div>
                <Button size="sm" variant="outline-primary" onClick={addPalletRow}>
                  + Add Pallet Config
                </Button>
              </div>
            </div>

            {safeArr(localPlan.palletPlan).length > 0 && (
              <>
                <Table bordered hover responsive striped className="mt-2 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '16%' }}>Coil Length (m)</th>
                      <th style={{ width: '18%' }}>Coils / Pallet</th>
                      <th style={{ width: '14%' }}>m / Pallet</th>
                      <th style={{ width: '13%' }}>Nr. Pallets</th>
                      <th style={{ width: '13%' }}>Total Coils</th>
                      <th style={{ width: '25%' }}>Notes</th>
                      <th style={{ width: '1%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {safeArr(localPlan.palletPlan).map((p, idx) => {
                      const rowCoils = (Number(p.coilsPerPallet) || 0) * (Number(p.palletCount) || 0);
                      const metersPerPallet = (Number(p.coilsPerPallet) || 0) * (Number(p.coilLength) || 0);
                      return (
                        <tr key={idx}>
                          <td>
                            <Form.Control type="number" value={p.coilLength}
                              onChange={e => updatePallet(idx, 'coilLength', e.target.value)} min={0} />
                          </td>
                          <td>
                            <Form.Control type="number" value={p.coilsPerPallet}
                              onChange={e => updatePallet(idx, 'coilsPerPallet', e.target.value)} min={0} />
                          </td>
                          <td className="text-end fw-semibold">
                            {metersPerPallet ? `${metersPerPallet.toLocaleString()} m` : '—'}
                          </td>
                          <td>
                            <Form.Control type="number" value={p.palletCount}
                              onChange={e => updatePallet(idx, 'palletCount', e.target.value)} min={0} />
                          </td>
                          <td className="text-end fw-semibold">{rowCoils || 0}</td>
                          <td>
                            <Form.Control value={p.notes || ''}
                              onChange={e => updatePallet(idx, 'notes', e.target.value)}
                              placeholder="Client notes..." />
                          </td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-danger" onClick={() => removePalletRow(idx)}>✖</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <div className="d-flex gap-3 small text-muted">
                  <span>Total coils on pallets: <strong className={totalPalletCoils === totalCoils ? 'text-success' : 'text-warning'}>{totalPalletCoils}</strong></span>
                  <span>Total coils ordered: <strong>{totalCoils}</strong></span>
                  {totalPalletCoils > 0 && totalPalletCoils !== totalCoils && (
                    <Badge bg="warning">Mismatch — {totalCoils - totalPalletCoils} coils unassigned</Badge>
                  )}
                  {totalPalletCoils > 0 && totalPalletCoils === totalCoils && (
                    <Badge bg="success">All coils assigned to pallets</Badge>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-body-tertiary">
        <div className="me-auto small text-muted">
          {target ? (
            <>
              Planned <b>{fmt(totalPlanned)}m</b> / Required{' '}
              <b>{fmt(target)}m</b>
            </>
          ) : (
            'Missing product total length'
          )}
        </div>

        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>

        <Button
          variant={isValidTotal ? 'success' : 'primary'}
          onClick={handleSave}
          disabled={!target}
        >
          {isValidTotal ? 'Save (OK)' : 'Save Delivery Plan'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeliveryPlanModal;
